import painDurationBelow6Weeks from '@shared/assets/img/pain_duration_below_6_weeks.svg'
import painDuration6To12Weeks from '@shared/assets/img/pain_duration_6_to_12_weeks.svg'
import painDurationOver12Weeks from '@shared/assets/img/pain_duration_over_12_weeks.svg'
import previousTreatmentYes from '@shared/assets/img/previous_treatment_yes.svg'
import previousTreatmentNo from '@shared/assets/img/previous_treatment_no.svg'
import pgicReflectionMuchImproved from '@shared/assets/img/pgic_reflection_much_improved.svg'
import pgicReflectionVeryMuchImproved from '@shared/assets/img/pgic_reflection_very_much_improved.svg'
// Both three-level questions reuse the same achieved / partly / not artwork.
import goalAchievementFull from '@shared/assets/img/goal_achievement_full.svg'
import goalAchievementPartial from '@shared/assets/img/goal_achievement_partial.svg'
import goalAchievementNone from '@shared/assets/img/goal_achievement_none.svg'

// Answer options for the renewal assessment questionnaires.
//
// The `key` values are the contract with the backend — they must match the
// answer_options in kaia-backend's questionnaires/pgic.yml and
// questionnaires/renewal_therapy_feedback.yml, because the API resolves the
// scored option by key. Order here is the order shown on screen.
//
// `label` is the option copy; it is both rendered and submitted as the answer's
// `text`, so it is functionally load-bearing rather than decorative.
// `reflectionTitle` / `reflectionBody` are the follow-up screen's copy for that
// answer — the merged reflection view looks them up from here.

// PGIC is presented as a labelled 7-point scale: `score` is both the number
// shown on the card and the backend's score for the option, and `color` names
// the scale dot's KDS token (see the __dot--* modifiers in
// RenewalAssessmentPGIC.vue).
// Every answer gets its own reflection screen — image, title and body.
export const PGIC_OVERALL_CHANGE_OPTIONS = [
  {
    key: 'very_much_improved',
    score: 7,
    color: 'on-green',
    label: 'eu__renewal_assessment__pgic__option_very_much_improved',
    description:
      'eu__renewal_assessment__pgic__option_very_much_improved__description',
    image: pgicReflectionVeryMuchImproved,
    imageWidth: 335,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__pgic__reflection_very_much_improved__title',
    reflectionBody:
      'eu__renewal_assessment__pgic__reflection_very_much_improved__body',
  },
  {
    key: 'much_improved',
    score: 6,
    color: 'green-accent',
    label: 'eu__renewal_assessment__pgic__option_much_improved',
    description:
      'eu__renewal_assessment__pgic__option_much_improved__description',
    image: pgicReflectionMuchImproved,
    imageWidth: 335,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__pgic__reflection_much_improved__title',
    reflectionBody:
      'eu__renewal_assessment__pgic__reflection_much_improved__body',
  },
  {
    key: 'minimally_improved',
    score: 5,
    color: 'green-strong',
    label: 'eu__renewal_assessment__pgic__option_minimally_improved',
    description:
      'eu__renewal_assessment__pgic__option_minimally_improved__description',
    image: painDuration6To12Weeks,
    imageWidth: 335,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__pgic__reflection_minimally_improved__title',
    reflectionBody:
      'eu__renewal_assessment__pgic__reflection_minimally_improved__body',
  },
  {
    key: 'no_change',
    score: 4,
    color: 'strong',
    label: 'eu__renewal_assessment__pgic__option_no_change',
    description: 'eu__renewal_assessment__pgic__option_no_change__description',
    image: previousTreatmentYes,
    imageWidth: 315,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__pgic__reflection_no_change__title',
    reflectionBody: 'eu__renewal_assessment__pgic__reflection_no_change__body',
  },
  {
    key: 'minimally_worse',
    score: 3,
    color: 'yellow-strong',
    label: 'eu__renewal_assessment__pgic__option_minimally_worse',
    description:
      'eu__renewal_assessment__pgic__option_minimally_worse__description',
    image: painDurationBelow6Weeks,
    imageWidth: 335,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__pgic__reflection_minimally_worse__title',
    reflectionBody:
      'eu__renewal_assessment__pgic__reflection_minimally_worse__body',
  },
  {
    key: 'much_worse',
    score: 2,
    color: 'orange-accent',
    label: 'eu__renewal_assessment__pgic__option_much_worse',
    description: 'eu__renewal_assessment__pgic__option_much_worse__description',
    image: previousTreatmentNo,
    imageWidth: 318,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__pgic__reflection_much_worse__title',
    reflectionBody: 'eu__renewal_assessment__pgic__reflection_much_worse__body',
  },
  {
    key: 'very_much_worse',
    score: 1,
    color: 'on-orange',
    label: 'eu__renewal_assessment__pgic__option_very_much_worse',
    description:
      'eu__renewal_assessment__pgic__option_very_much_worse__description',
    image: painDurationOver12Weeks,
    imageWidth: 335,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__pgic__reflection_very_much_worse__title',
    reflectionBody:
      'eu__renewal_assessment__pgic__reflection_very_much_worse__body',
  },
]

export const GOAL_ACHIEVEMENT_OPTIONS = [
  {
    key: 'fully_achieved',
    label: 'eu__renewal_assessment__goal_achievement__option_fully_achieved',
    description:
      'eu__renewal_assessment__goal_achievement__option_fully_achieved__description',
    image: goalAchievementFull,
    imageWidth: 335,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__goal_achievement__reflection_fully_achieved__title',
    reflectionBody:
      'eu__renewal_assessment__goal_achievement__reflection_fully_achieved__body',
  },
  {
    key: 'partially_achieved',
    label:
      'eu__renewal_assessment__goal_achievement__option_partially_achieved',
    description:
      'eu__renewal_assessment__goal_achievement__option_partially_achieved__description',
    image: goalAchievementPartial,
    imageWidth: 315,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__goal_achievement__reflection_partially_achieved__title',
    reflectionBody:
      'eu__renewal_assessment__goal_achievement__reflection_partially_achieved__body',
  },
  {
    key: 'not_achieved',
    label: 'eu__renewal_assessment__goal_achievement__option_not_achieved',
    description:
      'eu__renewal_assessment__goal_achievement__option_not_achieved__description',
    image: goalAchievementNone,
    imageWidth: 314,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__goal_achievement__reflection_not_achieved__title',
    reflectionBody:
      'eu__renewal_assessment__goal_achievement__reflection_not_achieved__body',
  },
]

export const THERAPY_HELPFULNESS_OPTIONS = [
  {
    key: 'helped',
    label: 'eu__renewal_assessment__therapy_feedback__option_helped',
    description:
      'eu__renewal_assessment__therapy_feedback__option_helped__description',
    image: goalAchievementFull,
    imageWidth: 335,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__therapy_feedback__reflection_helped__title',
    reflectionBody:
      'eu__renewal_assessment__therapy_feedback__reflection_helped__body',
  },
  {
    key: 'somewhat_helped',
    label: 'eu__renewal_assessment__therapy_feedback__option_somewhat_helped',
    description:
      'eu__renewal_assessment__therapy_feedback__option_somewhat_helped__description',
    image: goalAchievementPartial,
    imageWidth: 315,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__therapy_feedback__reflection_somewhat_helped__title',
    reflectionBody:
      'eu__renewal_assessment__therapy_feedback__reflection_somewhat_helped__body',
  },
  {
    key: 'not_helped',
    label: 'eu__renewal_assessment__therapy_feedback__option_not_helped',
    description:
      'eu__renewal_assessment__therapy_feedback__option_not_helped__description',
    image: goalAchievementNone,
    imageWidth: 314,
    imageHeight: 259,
    reflectionTitle:
      'eu__renewal_assessment__therapy_feedback__reflection_not_helped__title',
    reflectionBody:
      'eu__renewal_assessment__therapy_feedback__reflection_not_helped__body',
  },
]

// The goal vocabulary itself lives in selfTestOptions (GOAL_OPTIONS,
// GOAL_KEY_OTHER) — it is shared with SelfTestGoal, which is where those keys
// originate.
